import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Public banner rotator shown at the bottom of the landing + dashboard pages.
 * Hits /api/banners/rotate which picks a random active banner from an owner
 * with impressions to spend, charges one impression to that owner, and
 * returns the banner. Also fetches a text ad from /api/text-ads/rotate and
 * renders it as a colorful strip attached to the bottom of the banner.
 */
export default function BannerAd() {
  const [banner, setBanner] = useState(null);
  const [textAd, setTextAd] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [banRes, txtRes] = await Promise.allSettled([
          api.get("/banners/rotate"),
          api.get("/text-ads/rotate"),
        ]);
        if (cancelled) return;
        if (banRes.status === "fulfilled" && banRes.value?.data?.banner) setBanner(banRes.value.data.banner);
        if (txtRes.status === "fulfilled" && txtRes.value?.data?.ad) setTextAd(txtRes.value.data.ad);
      } catch {
        /* silent — ads are optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!banner && !textAd) return null;

  const clickUrl = banner ? `${process.env.REACT_APP_BACKEND_URL}/api/banners/${banner.id}/click` : null;
  const textClickUrl = textAd ? `${process.env.REACT_APP_BACKEND_URL}/api/text-ads/${textAd.id}/click` : null;

  return (
    <div data-testid="banner-ad-slot" className="w-full flex justify-center px-4 py-6">
      <div className="w-full" style={{ maxWidth: 728 }}>
        {banner && (
          <a
            href={clickUrl}
            target="_blank"
            rel="noreferrer noopener sponsored"
            data-testid="banner-ad-link"
            className="block rounded-t-md overflow-hidden shadow hover:shadow-lg transition-shadow"
            style={{ borderRadius: textAd ? "0.375rem 0.375rem 0 0" : "0.375rem" }}
          >
            <img
              src={banner.image_url}
              alt="Advertisement"
              width={728}
              height={90}
              className="block w-full"
              style={{ maxWidth: 728, height: 90, objectFit: "cover" }}
            />
          </a>
        )}
        {textAd && (
          <a
            href={textClickUrl}
            target="_blank"
            rel="noreferrer noopener sponsored"
            data-testid="text-ad-link"
            className="block text-center text-white font-bold text-sm tracking-wide px-3 py-1.5 shadow hover:brightness-110 transition-all bg-gradient-to-r from-cyan-500 via-blue-500 to-fuchsia-500"
            style={{ borderRadius: banner ? "0 0 0.375rem 0.375rem" : "0.375rem" }}
            title={textAd.text}
          >
            {textAd.text}
          </a>
        )}
      </div>
    </div>
  );
}
